import { AutoScroll } from "@/directives/auto-scroll"
import { canPermission } from "@/directives/can-permission"
import { clickOutside } from "@/directives/click-outside"
import { confirm } from "@/directives/confirm"
import { CssVariables } from "@/directives/css-variables"
import { fitText } from "@/directives/fit-text"
import { Highlight } from "@/directives/highlight"
import { hover } from "@/directives/hover"
import { tooltipTitle } from "@/directives/tooltip-title"
import {
	App as VueApp,
	ComponentPublicInstance,
	createApp as createVueApp,
} from "vue"
import { vue3Debounce } from "vue-debounce"
import { Base } from "./base"
import { EventDispatcher } from "./event-dispatcher"
import { can, info, token, translate, user } from "./init"
import { projectInfo, warnInfo, errorInfo } from "./log"

declare var window: any

window.codendot = window.codendot ?? {}
window.codendot.vueApps = window.codendot.vueApps ?? {}

type Events = {
	init: EventDispatcher
	start: EventDispatcher
	mount: EventDispatcher
	mounted: EventDispatcher
}

let first: boolean = false

class Application extends Base<Events> {
	constructor(public name: string, public component: any) {
		super()
	}

	mount() {
		this.events.init.dispatch({ sender: this })
		window.codendot.vueApps[this.name] = (
			id: string,
			params: { [name: string]: any },
		): Promise<VueApp<Element>> => {
			return new Promise((resolve) => {
				if (!first) {
					first = true
					projectInfo()
				}
				this.events.start.dispatch({ sender: this })
				const app: VueApp<Element> = createVueApp(this.component, params)
				app.config.globalProperties.$params = params
				this.properties(app)
				this.directives(app)
				this.mixins(app)
				this.components(app)
				this.plugins(app)
				app.provide("app", app)

				// On Warning
				app.config.warnHandler = (
					msg: string,
					instance: ComponentPublicInstance | null,
					trace: string,
				) => {
					warnInfo(msg, instance, trace)
				}

				// On Error
				app.config.errorHandler = (
					err: any,
					instance: ComponentPublicInstance | null,
					info: string,
				) => {
					errorInfo(err, instance, info)
				}

				this.events.mount.dispatch({ vue: app, sender: this })
				app.mount(`#${id}`)
				this.events.mounted.dispatch({ vue: app, sender: this })
				resolve(app)
			})
		}
	}

	protected properties(app: VueApp<Element>) {
		app.config.globalProperties.$info = info
		app.config.globalProperties.$user = user
		app.config.globalProperties.$token = token
		app.config.globalProperties.$translate = translate
		app.config.globalProperties.$can = can
		app.config.globalProperties.$env = () => info().env
		app.config.globalProperties.$id = () => info().id
		app.config.globalProperties.$isAuth = () => info().isAuth
		app.config.globalProperties.$isGuest = () => info().isGuest
		app.config.globalProperties.$language = () => info().language
		app.config.globalProperties.$name = () => info().name
		app.config.globalProperties.$tier = () => info().tier
		app.config.globalProperties.$url = () => info().url
		app.config.globalProperties.$isEn = () => info().language === "en"
		app.config.globalProperties.$isAr = () => info().language === "ar"
	}

	protected directives(app: VueApp<Element>) {
		app.directive("click-outside", clickOutside)
		app.directive("can", canPermission)
		app.directive("confirm", confirm)
		app.directive("css-vars", CssVariables)
		app.directive("hover", hover)
		app.directive("tooltip", tooltipTitle)
		app.directive("highlight", Highlight)
		app.directive("auto-scroll", AutoScroll)
		app.directive("fit-text", fitText)
		app.directive("debounce", vue3Debounce({ lock: true }))
	}

	protected mixins(app: VueApp<Element>) {}

	protected components(app: VueApp<Element>) {}

	protected plugins(app: VueApp<Element>) {}
}

export type App = InstanceType<typeof Application>

export function createApp(name: string, component: any) {
	return new Application(name, component)
}
